import type { Microsoft365StatusDto } from '@dbrain/shared';
import { Button, ConfirmDialog, Icons, Menu, MenuItem, MenuSeparator, Switch } from '@dbrain/ui';
import { useId, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { isApiError } from '../../api/client.js';
import { useMicrosoft365Disconnect, useMicrosoft365SetEnabled } from '../../api/microsoft365.js';
import { ErrorAlert } from '../ErrorAlert.js';
import { Microsoft365ConnectDialog } from './ConnectDialog.js';
import { MicrosoftIcon } from './MicrosoftIcon.js';
import { ConsentRequestButton, Microsoft365Products, Microsoft365Tools } from './parts.js';
import { cardView, cardWords, offReason } from './state.js';

/**
 * The Microsoft 365 card of Bağlantılar' "Hazır bağlantılar" (MsBaglantilar, MsBaglandi, MsDurumlar): Microsoft's own
 * Microsoft 365 product icon in the tile right before the name (v0.15g.1, D-254; never Microsoft's four-square logo),
 * the state in words, what it is for, the products with Microsoft's icons, and the one thing to do next: "Hesabımı bağla", "Yeniden bağlan", "Yöneticiye
 * bildir". Connected: the person's own switch, "…" (Yeniden bağlan, Bağlantıyı kaldır) and "Ayrıntılar" with the
 * six read tools (and part 3's write tools of the switches that are on). Turned off by the firm or a team: a quiet
 * card that says why. Not set up: only admins see it, with the way to Yönetim › Entegrasyonlar.
 */
export function Microsoft365Card({
  status,
  workspaceId,
  unavailable,
  onConnected,
}: {
  status: Microsoft365StatusDto;
  /** the page's "Anahtarlar şurada geçerli": this switch is the person's own everywhere */
  workspaceId: string;
  /** the last sign-in came back with "the server cannot reach Microsoft" */
  unavailable: boolean;
  /** a code sign-in finished in the dialog (the page shows the success line) */
  onConnected: () => void;
}) {
  const { t } = useTranslation();
  const detailsId = useId();
  const [connecting, setConnecting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [details, setDetails] = useState(false);
  const [unreachable, setUnreachable] = useState(false);
  const disconnect = useMicrosoft365Disconnect();
  const setEnabled = useMicrosoft365SetEnabled();
  const view = cardView(status, unavailable || unreachable);
  if (view.kind === 'hidden') return null;
  const email = status.account?.email ?? null;
  const connect = () => {
    setUnreachable(false);
    setConnecting(true);
  };
  // part 3: with sending or meetings on, never "yalnız okur"; what it is for follows the switches (MsBaglantilar)
  const words = cardWords(status.features);
  const write = words.writes ? 'Write' : '';
  const foot =
    view.kind === 'setup'
      ? t('microsoft365.user.card.setupNote')
      : view.kind === 'connected' && email
        ? t(`microsoft365.user.card.foot.connected${write}`, { email })
        : email
          ? t(`microsoft365.user.card.foot.account${write}`, { email })
          : view.kind === 'off'
            ? t(`microsoft365.user.card.foot.readOnly${write}`)
            : t('microsoft365.user.card.foot.open');
  return (
    <li className={view.muted ? 'connector-card m365-card m365-card--muted' : 'connector-card m365-card'} data-testid="microsoft365-card" data-state={status.state} data-view={view.kind}>
      <div className="connector-card__head m365-card__head">
        <span className="m365-tile" aria-hidden="true">
          <MicrosoftIcon product="m365" size={20} />
        </span>
        <div className="connector-card__title">
          <div className="m365-card__name">
            <h3>{t('microsoft365.user.name')}</h3>
            <span className={`connector-state connector-state--${view.tone}`} data-testid="microsoft365-state">
              {t(`microsoft365.user.card.state.${view.pill}`)}
            </span>
          </div>
          <p className="m365-card__description">{t(`microsoft365.user.card.${words.description}`)}</p>
        </div>
        {view.action === 'connect' || view.action === 'reconnect' || view.action === 'retry' || view.toggle || view.menu.reconnect || view.menu.remove ? (
          <div className="m365-card__actions">
            {view.action === 'connect' || view.action === 'reconnect' || view.action === 'retry' ? (
              <Button
                icon={view.action === 'connect' ? undefined : <Icons.regenerate size={16} />}
                onClick={connect}
                data-testid={view.action === 'connect' ? 'microsoft365-connect' : view.action === 'reconnect' ? 'microsoft365-reconnect' : 'microsoft365-retry'}
              >
                {t(view.action === 'connect' ? 'microsoft365.user.card.connect' : view.action === 'reconnect' ? 'microsoft365.user.card.reconnect' : 'microsoft365.user.card.retry')}
              </Button>
            ) : null}
            {view.toggle ? (
              <Switch
                checked={status.enabled}
                disabled={setEnabled.isPending}
                onChange={(e) => setEnabled.mutate(e.target.checked)}
                aria-label={t('microsoft365.user.card.toggle')}
                data-testid="microsoft365-toggle"
              />
            ) : null}
            {view.menu.reconnect || view.menu.remove ? (
              <Menu align="right" label={t('microsoft365.user.card.menu')} triggerClassName="ui-icon-button ui-icon-button--sm" trigger={<Icons.more size={16} />} data-testid="microsoft365-menu">
                {view.menu.reconnect ? (
                  <MenuItem icon={<Icons.regenerate />} onClick={connect} data-testid="microsoft365-menu-reconnect">
                    {t('microsoft365.user.card.reconnect')}
                  </MenuItem>
                ) : null}
                {view.menu.reconnect && view.menu.remove ? <MenuSeparator /> : null}
                {view.menu.remove ? (
                  <MenuItem danger icon={<Icons.trash />} onClick={() => setRemoving(true)} data-testid="microsoft365-remove">
                    {t('microsoft365.user.card.remove')}
                  </MenuItem>
                ) : null}
              </Menu>
            ) : null}
          </div>
        ) : null}
      </div>
      {view.kind !== 'off' && view.kind !== 'setup' ? <Microsoft365Products features={status.features} connected={view.kind === 'connected' && status.enabled} /> : null}
      <CardLine status={status} kind={view.kind} />
      {view.toggle && workspaceId ? <p className="m365-card__line m365-card__line--quiet">{t('microsoft365.user.card.everywhere')}</p> : null}
      <div className="connector-card__foot">
        <span className="connector-card__used" data-testid="microsoft365-foot">
          {foot}
        </span>
        {view.details ? (
          <button
            type="button"
            className="link-button connector-card__details"
            aria-expanded={details}
            aria-controls={details ? detailsId : undefined}
            onClick={() => setDetails((v) => !v)}
            data-testid="microsoft365-details"
          >
            {t('microsoft365.user.card.details')}
          </button>
        ) : null}
      </div>
      {details && view.details ? (
        <div id={detailsId} className="connector-card__more" data-testid="microsoft365-more">
          <Microsoft365Tools status={status} />
        </div>
      ) : null}
      <ErrorAlert error={setEnabled.error} />
      {connecting ? (
        <Microsoft365ConnectDialog
          returnTo="/connectors"
          onClose={() => setConnecting(false)}
          onConnected={onConnected}
          onError={(error) => setUnreachable(isApiError(error, 'microsoft_unavailable'))}
        />
      ) : null}
      {removing ? (
        <ConfirmDialog
          title={t('microsoft365.user.card.removeTitle')}
          confirmLabel={t('microsoft365.user.card.remove')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={disconnect.isPending}
          error={<ErrorAlert error={disconnect.error} />}
          onConfirm={() => disconnect.mutate(undefined, { onSuccess: () => setRemoving(false) })}
          onClose={() => {
            setRemoving(false);
            disconnect.reset();
          }}
          data-testid="microsoft365-remove-dialog"
        >
          {t('microsoft365.user.card.removeBody')}
        </ConfirmDialog>
      ) : null}
    </li>
  );
}

/** The card's one line on what is going on: why it is off, what to renew, whom to tell. */
function CardLine({ status, kind }: { status: Microsoft365StatusDto; kind: ReturnType<typeof cardView>['kind'] }) {
  const { t } = useTranslation();
  switch (kind) {
    case 'setup':
      return (
        <p className="m365-card__line" data-testid="microsoft365-setup">
          <Icons.settings size={16} />
          <span>
            <Trans i18nKey="microsoft365.user.card.setup" components={{ go: <Link to="/admin/integrations" data-testid="microsoft365-setup-link" /> }} />
          </span>
        </p>
      );
    case 'off':
      return (
        <p className="m365-card__line" data-testid="microsoft365-off">
          <Icons.lock size={16} />
          <span>{t(`microsoft365.user.card.off.${offReason(status)}`, { team: status.teamName ?? '' })}</span>
        </p>
      );
    case 'reconnect':
      return (
        <p className="m365-card__line m365-card__line--warning">
          <Icons.regenerate size={16} />
          <span>{t('microsoft365.user.card.body.reconnect')}</span>
        </p>
      );
    case 'consent':
      return (
        <div className="m365-card__line m365-card__line--warning">
          <Icons.shield size={16} />
          <div className="m365-card__line-body">
            <span>{t('microsoft365.user.card.body.consent')}</span>
            <ConsentRequestButton />
          </div>
        </div>
      );
    case 'unavailable':
      return (
        <p className="m365-card__line m365-card__line--danger" data-testid="microsoft365-unavailable">
          <Icons.info size={16} />
          <span>
            {status.canManage ? (
              <Trans i18nKey="microsoft365.user.card.body.unavailableAdmin" components={{ go: <Link to="/admin/integrations" /> }} />
            ) : (
              t('microsoft365.user.card.body.unavailable')
            )}
          </span>
        </p>
      );
    case 'connected':
      return status.enabled ? null : (
        <p className="m365-card__line m365-card__line--quiet" data-testid="microsoft365-switched-off">
          {t('microsoft365.user.card.body.switchedOff')}
        </p>
      );
    default:
      return null;
  }
}
